"use client";

// DIRECTIVE-040 commit 5: live ZIP -> fee preview for the signup
// wizard + any other public surface that wants to surface "what
// would this client pay for delivery". Fetches /api/delivery/
// effective-fee?zip=X (the prospect path — signed-out). Renders
// the fee + zone label + a friendly explainer.
//
// Per Q5 lock: pickup mode renders the explicit $0 line + Doral
// pickup explainer instead of hiding. Invalid / non-FL ZIPs get
// the "free pickup at Doral" message.

import { useEffect, useRef, useState } from "react";

// PASTE #235: callback so a parent (the signup wizard) can gate an
// out-of-state fulfillment section on the SAME determination this
// component already makes, without a second fetch.

export type DeliveryFeePreviewStrings = {
  loading: string;
  errorPrefix: string;
  // "We deliver direct to {county}, FL — flat ${fee}."
  deliveryTemplate: string;
  // "We deliver to all of Florida. Out of state, pick up free in Doral OR ship to your trucker."
  outOfStateExplainer: string;
  // "Free pickup in Doral (Miami), Mon–Sun 7am–2pm."
  pickupExplainer: string;
};

const EN_STRINGS: DeliveryFeePreviewStrings = {
  loading: "Checking your zone…",
  errorPrefix: "Couldn't check that ZIP",
  deliveryTemplate: "We deliver direct to {county}, FL — flat ${fee}.",
  // Alexis polish lock Jun 17: drop comma after "Outside Florida",
  // change "you can ship free" -> "we can ship free", insert "you
  // can" before "pick up". Single source of truth for this string
  // so every surface that uses DeliveryFeePreview gets the polish.
  outOfStateExplainer:
    "We deliver direct to every Florida ZIP. Outside Florida we deliver to your Miami trucker's facility OR you can pick up at our Doral pickup location (Mon–Sun 7am–2pm).",
  pickupExplainer:
    "Order-ahead pickup in Doral (Miami), Mon–Sun 7am–2pm.",
};

type EffectiveFeeBody = {
  fee: number;
  zone: number;
  reason: "pickup" | "override" | "same-day-no-double" | "zone";
  county: string | null;
  zip: string | null;
  deliveryMode: "delivery" | "pickup";
};

type EffectiveFeeResponse = {
  ok: boolean;
  effectiveFee: EffectiveFeeBody;
};

const ZIP_RE = /^\d{5}$/;

export function DeliveryFeePreview({
  zip,
  strings = EN_STRINGS,
  testIdPrefix = "delivery-preview",
  onOutOfStateChange,
}: {
  zip: string;
  strings?: DeliveryFeePreviewStrings;
  testIdPrefix?: string;
  onOutOfStateChange?: (isOutOfState: boolean) => void;
}) {
  const [data, setData] = useState<EffectiveFeeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  const cleanZip = zip.trim().split("-")[0];
  const valid = ZIP_RE.test(cleanZip);

  // Out-of-state = the fee resolver fell back to Doral pickup (any ZIP
  // not in the Florida table). Report it up whenever it changes.
  const isOutOfState =
    valid && data
      ? data.effectiveFee.reason === "pickup" &&
        data.effectiveFee.deliveryMode === "pickup"
      : false;
  useEffect(() => {
    onOutOfStateChange?.(isOutOfState);
  }, [isOutOfState, onOutOfStateChange]);

  useEffect(() => {
    if (!valid) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- MKT-#82(e): intentional reset of derived fetch state when inputs go invalid; client-only, shipped behavior. Refactor tracked separately.
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    fetch(
      `/api/delivery/effective-fee?zip=${encodeURIComponent(cleanZip)}`,
      { credentials: "include" },
    )
      .then(async (r) => {
        if (reqId !== reqIdRef.current) return;
        if (!r.ok) {
          setError(`${strings.errorPrefix} (HTTP ${r.status})`);
          setLoading(false);
          return;
        }
        const json = (await r.json()) as EffectiveFeeResponse;
        setData(json);
        setLoading(false);
      })
      .catch(() => {
        if (reqId !== reqIdRef.current) return;
        setError(`${strings.errorPrefix} (network)`);
        setLoading(false);
      });
  }, [valid, cleanZip, strings.errorPrefix]);

  if (!valid) return null;
  if (loading && !data) {
    return (
      <p
        data-testid={`${testIdPrefix}-loading`}
        className="mt-2 text-xs text-zinc-500"
      >
        {strings.loading}
      </p>
    );
  }
  if (error) {
    // URGENT signup fix: this is an INFORMATIONAL delivery-zone preview, not a
    // validation control. A transient fetch failure (e.g. the /api 503s Cowork
    // saw) must NEVER render as a red alarm under the address - a non-developer
    // reads that as "my address is invalid" even though the field is valid and
    // submit is not gated by this. Show a neutral, muted note instead; the zone
    // is confirmed server-side after submit.
    return (
      <p
        data-testid={`${testIdPrefix}-error`}
        className="mt-2 text-xs text-zinc-400"
      >
        We&apos;ll confirm your delivery zone after you submit.
      </p>
    );
  }
  if (!data) return null;

  const e = data.effectiveFee;

  return (
    <div
      data-testid={testIdPrefix}
      data-fee={e.fee}
      data-zone={e.zone}
      data-reason={e.reason}
      data-delivery-mode={e.deliveryMode}
      className="mt-2 rounded-md border border-brand-teal/40 bg-brand-teal-soft px-3 py-2 text-xs text-brand-teal-dark"
    >
      {isOutOfState ? (
        <p data-testid={`${testIdPrefix}-out-of-state`}>
          {strings.outOfStateExplainer}
        </p>
      ) : e.deliveryMode === "pickup" ? (
        <p data-testid={`${testIdPrefix}-pickup`}>{strings.pickupExplainer}</p>
      ) : (
        <p data-testid={`${testIdPrefix}-delivery`}>
          {strings.deliveryTemplate
            .replace("{county}", e.county ?? "Florida")
            .replace("{fee}", String(e.fee))}
        </p>
      )}
    </div>
  );
}
